import { CompactShape } from "../model/realtime/CompactShape";
import { CompactTimeline } from "../model/realtime/CompactTimeline";
import type { TripScheduleWire } from "../types/dtos/realtime-api/realtime.dto";

/**
 * Builds a trip timeline aligned to a shape slice and stop schedule.
 *
 * This is a one-shot operation performed when a new trip arrives.
 * Cost: ~0.01ms per trip (~2ms for 200 trips).
 *
 * For each consecutive stop pair, timestamps are interpolated over the
 * corresponding shape points using cumulative distance (with an index-based
 * fallback when distance is flat). Stop points are explicitly marked, then
 * undefined gaps are removed and timestamps are forced to be strictly increasing.
 *
 * @param shape Full route shape (shared cache entry for the trip's `shape_id`).
 * @param schedule Trip schedule containing shape bounds and ordered stops.
 * @returns Dense columnar timeline for the selected shape segment, or empty columns when
 * input data cannot produce a valid timeline.
 */
export function buildTimeline(
    shape: CompactShape,
    schedule: TripScheduleWire,
): CompactTimeline {
    const { shape_start_idx, shape_end_idx, stops } = schedule;

    if ( stops.length < 2 || shape.length === 0 ) {
        return emptyCompactTimeline();
    }

    const startIdx = Math.max( 0, shape_start_idx );
    const endIdx = Math.min( shape.length - 1, shape_end_idx );

    // Zero-copy slice: local indices 0..view.length-1 map to global shape indices [startIdx, endIdx].
    const view = shape.slice( startIdx, endIdx );

    if ( view.length === 0 ) {
        return emptyCompactTimeline();
    }

    const sparseTimestamp: ( number | undefined )[] = new Array<number | undefined>( view.length );
    const sparseLat: ( number | undefined )[] = new Array<number | undefined>( view.length );
    const sparseLon: ( number | undefined )[] = new Array<number | undefined>( view.length );
    const sparseBearing: ( number | undefined )[] = new Array<number | undefined>( view.length );
    const sparseStopSeq: ( number | undefined )[] = new Array<number | undefined>( view.length );
    const sparseIsStop: ( boolean | undefined )[] = new Array<boolean | undefined>( view.length );

    for ( let k = 0; k < stops.length - 1; k++ ) {
        const stopK = stops[k];
        const stopK1 = stops[k + 1];

        const idxStart = stopK.shape_idx - startIdx;
        const idxEnd = stopK1.shape_idx - startIdx;

        if ( idxStart < 0 || idxEnd >= view.length || idxStart > idxEnd ) {
            continue;
        }

        const tStart = stopK.dep;
        const tEnd = stopK1.arr;

        const distStart = view.cumDistAt( idxStart );
        const distEnd = view.cumDistAt( idxEnd );
        const totalDist = distEnd - distStart;

        for ( let j = idxStart; j <= idxEnd; j++ ) {
            const ratio = totalDist > 0
                ? ( view.cumDistAt( j ) - distStart ) / totalDist
                : idxEnd > idxStart ? ( j - idxStart ) / ( idxEnd - idxStart ) : 0;

            sparseTimestamp[j] = tStart + ratio * ( tEnd - tStart );
            sparseLat[j] = view.latAt( j );
            sparseLon[j] = view.lonAt( j );
            sparseBearing[j] = view.bearingX10At( j ) / 10;
            sparseStopSeq[j] = 0;
            sparseIsStop[j] = false;
        }

        /*
         * Mark the stop vertex. Use departure time, not arrival: InterpolationEngine maps
         * real time -> scheduled time using stop.dep anchors only; mixing arr here makes
         * the bus move along the first segment during dwell (arr..dep) before real departure.
         */
        if ( sparseTimestamp[idxStart] !== undefined ) {
            sparseIsStop[idxStart] = true;
            sparseStopSeq[idxStart] = stopK.seq;
            sparseTimestamp[idxStart] = stopK.dep;
        }
    }

    const lastStop = stops[stops.length - 1];
    const lastIdx = lastStop.shape_idx - startIdx;
    if ( lastIdx >= 0 && lastIdx < view.length && sparseTimestamp[lastIdx] !== undefined ) {
        sparseIsStop[lastIdx] = true;
        sparseStopSeq[lastIdx] = lastStop.seq;
        sparseTimestamp[lastIdx] = lastStop.dep;
    }

    // Dense timeline length is often < view.length after dropping undefined sparse slots.
    const denseLength = sparseTimestamp.filter( ( v ) => v !== undefined ).length;
    if ( denseLength < 2 ) {
        return emptyCompactTimeline();
    }

    const timestampS = new Float64Array( denseLength );
    const lat = new Float64Array( denseLength );
    const lon = new Float64Array( denseLength );
    const bearing = new Float64Array( denseLength );
    const stopSequence = new Uint16Array( denseLength );
    const isStop = new Uint8Array( denseLength );

    let out = 0;
    for ( let j = 0; j < view.length; j += 1 ) {
        const ts = sparseTimestamp[j];
        if ( ts === undefined || sparseLat[j] === undefined || sparseLon[j] === undefined ) {
            continue;
        }

        if ( out > 0 && ts <= timestampS[out - 1] ) {
            timestampS[out] = timestampS[out - 1] + 0.001;
        } else {
            timestampS[out] = ts;
        }
        lat[out] = sparseLat[j]!;
        lon[out] = sparseLon[j]!;
        bearing[out] = sparseBearing[j] ?? 0;
        stopSequence[out] = sparseStopSeq[j] ?? 0;
        isStop[out] = sparseIsStop[j] ? 1 : 0;
        out += 1;
    }

    if ( out < 2 ) {
        return emptyCompactTimeline();
    }

    return new CompactTimeline(
        out,
        timestampS,
        lat,
        lon,
        bearing,
        stopSequence,
        isStop,
    );
}

/**
 * @returns An empty timeline with no points (interpolation treats as invalid).
 */
function emptyCompactTimeline(): CompactTimeline {
    return new CompactTimeline(
        0,
        new Float64Array( 0 ),
        new Float64Array( 0 ),
        new Float64Array( 0 ),
        new Float64Array( 0 ),
        new Uint16Array( 0 ),
        new Uint8Array( 0 ),
    );
}
